/**
 * The signed-in session: the tokens, who they name, and keeping them fresh.
 *
 * A small external store (subscribe / getSession — what React's
 * `useSyncExternalStore` reads), deliberately outside React: the API client's
 * token callback (`idToken`) must work from any request, not only from a
 * component render.
 *
 * - **Persistence** is one localStorage entry per auth target (`storageKey`),
 *   validated on read (it is data another build may have written, CLAUDE.md
 *   branch 2). Tokens in localStorage are readable by any script on the
 *   origin — the SPA's CSP and the absence of third-party script are what
 *   that rests on, as for any SPA holding a bearer token.
 * - **Refresh** happens when the ID token is within `REFRESH_SKEW_MS` of
 *   expiry, or when the API client forces it after a 401. Concurrent callers
 *   share one refresh. A refresh that is REFUSED, or a session that cannot be
 *   refreshed (a node's dev token), signs out and the route guard sends the
 *   user to the login page; one that fails transiently (a Wi-Fi blip, a
 *   Cognito 5xx) fails only that request, so an operator is never logged out
 *   mid-show by a network hiccup.
 * - **Other tabs**: `reload()` re-reads storage on the browser's `storage`
 *   event, so a sign-out in one tab is not written back by another's refresh.
 *   Sign-out is local (the refresh token is not revoked server-side).
 */
import { z } from 'zod';
import { readTokenClaims } from '../lib/jwt.js';

/** Refresh this long before expiry, so a token never expires in flight (and clocks may disagree a little). */
export const REFRESH_SKEW_MS = 60_000;

export const sessionSchema = z
  .object({
    provider: z.enum(['cognito', 'dev']),
    idToken: z.string().min(1),
    accessToken: z.string().min(1).nullable(),
    refreshToken: z.string().min(1).nullable(),
    userId: z.string().min(1),
    tenantId: z.string().min(1).nullable(),
    email: z.string().nullable(),
    expiresAtMs: z.number(),
  })
  .strict();

export type Session = z.infer<typeof sessionSchema>;

/** A session for these tokens, identity read off the ID token; null when it is not a usable JWT. */
export function sessionFromTokens(provider: Session['provider'], idToken: string, accessToken: string | null, refreshToken: string | null): Session | null {
  const claims = readTokenClaims(idToken);
  if (claims === null) return null;
  return { provider, idToken, accessToken, refreshToken, userId: claims.userId, tenantId: claims.tenantId, email: claims.email, expiresAtMs: claims.expiresAtMs };
}

/** Key/value storage (localStorage, guarded by the caller's adapter: it may be absent or throw). */
export type StoragePort = {
  readonly get: (key: string) => string | null;
  readonly set: (key: string, value: string) => void;
  readonly remove: (key: string) => void;
};

/**
 * Renew a session: the renewed session; null when renewal is REFUSED (revoked,
 * expired, not renewable: the user signs in again); or a rejection when the
 * attempt failed transiently (offline, 5xx, throttled: the session stands).
 */
export type Refresher = (session: Session) => Promise<Session | null>;

export class NotSignedInError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotSignedInError';
  }
}

export type SessionStore = {
  readonly getSession: () => Session | null;
  readonly subscribe: (listener: () => void) => () => void;
  readonly signIn: (session: Session) => void;
  readonly signOut: () => void;
  /** The ID token to send, refreshed first when near expiry or `force`d. Throws `NotSignedInError`, or the refresh's own error when it failed transiently. */
  readonly idToken: (force: boolean) => Promise<string>;
  /** Re-read storage after another tab changed it (the browser's `storage` event; main.tsx wires it). */
  readonly reload: () => void;
};

export type SessionStoreDeps = {
  readonly storage: StoragePort;
  readonly storageKey: string;
  readonly refresh: Refresher;
  readonly nowMs: () => number;
};

function readStored(storage: StoragePort, key: string): Session | null {
  const raw = storage.get(key);
  if (raw === null) return null;
  try {
    const parsed = sessionSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function createSessionStore(deps: SessionStoreDeps): SessionStore {
  let session = readStored(deps.storage, deps.storageKey);
  const listeners = new Set<() => void>();
  // One refresh at a time PER SESSION: a refresh started for a session that
  // has since been replaced is never handed to the new session's callers.
  let inFlight: { readonly for: Session; readonly result: Promise<Session | null> } | null = null;

  const notify = (): void => {
    for (const listener of listeners) listener();
  };

  const set = (next: Session | null): void => {
    session = next;
    if (next === null) deps.storage.remove(deps.storageKey);
    else deps.storage.set(deps.storageKey, JSON.stringify(next));
    notify();
  };

  const refreshOnce = (current: Session): Promise<Session | null> => {
    if (inFlight !== null && inFlight.for === current) return inFlight.result;
    const result = deps.refresh(current).finally(() => {
      if (inFlight?.result === result) inFlight = null;
    });
    inFlight = { for: current, result };
    return result;
  };

  const idToken = async (force: boolean): Promise<string> => {
    const current = session;
    if (current === null) throw new NotSignedInError('not signed in');
    if (!force && current.expiresAtMs - REFRESH_SKEW_MS > deps.nowMs()) return current.idToken;
    // A refresh that THROWS (network, 5xx, throttling) fails this request and
    // keeps the session: the refresh token is still good and the next request
    // retries. Only a refusal (null: revoked, expired, none) signs out.
    const renewed = await refreshOnce(current);
    // Signed in or out meanwhile: answer for the session that is current now.
    if (session !== current) return idToken(false);
    if (renewed === null) {
      set(null);
      throw new NotSignedInError('the session expired; sign in again');
    }
    set(renewed);
    return renewed.idToken;
  };

  return {
    getSession: () => session,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    signIn: (next) => set(next),
    signOut: () => set(null),
    idToken,
    reload: () => {
      // Another tab signed in, out or refreshed: take what storage holds now,
      // without writing it back (writing back is what would undo its sign-out).
      const stored = readStored(deps.storage, deps.storageKey);
      if (JSON.stringify(stored) === JSON.stringify(session)) return;
      session = stored;
      notify();
    },
  };
}
